/**
 * MobileNav — Full-screen slide-over menu for mobile.
 * Uses a proper overlay pattern with focus trap and server auth hook checks.
 */

import { Link } from "@tanstack/react-router";
import { X, Shield } from "lucide-react";
import { useEffect } from "react";
import { mainNav } from "@/config/navigation";
import { useCurrentUser } from "@/hooks/use-auth";

import logoImg from "@/assets/logo.webp";

type MobileNavProps = {
  open: boolean;
  onClose: () => void;
};

export function MobileNav({ open, onClose }: MobileNavProps) {
  const { isAuthenticated, isAdmin } = useCurrentUser();

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[100] bg-black/40 backdrop-blur-xs transition-opacity duration-300 md:hidden ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className={`fixed inset-y-0 left-0 z-[101] w-full max-w-sm bg-white text-stone-900 shadow-2xl transition-transform duration-300 ease-out md:hidden ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-5 py-4">
            <Link to="/" onClick={onClose} className="inline-block">
              <img
                src={logoImg}
                alt="Aster Decorz"
                className="h-12 w-auto max-w-[220px] object-contain drop-shadow-sm"
              />
            </Link>
            <button
              onClick={onClose}
              aria-label="Close menu"
              className="grid min-h-11 min-w-11 place-items-center"
            >
              <X className="h-5 w-5" strokeWidth={1.25} />
            </button>
          </div>

          {/* Navigation links */}
          <nav className="flex-1 overflow-y-auto px-5 py-6">
            <ul className="space-y-1">
              {mainNav.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={onClose}
                    className="flex items-center justify-between py-4 border-b border-border/50 font-display text-2xl transition-colors hover:text-primary"
                  >
                    {item.label}
                  </Link>
                  {item.children && (
                    <ul className="pb-2">
                      {item.children.map((child) => (
                        <li key={child.to}>
                          <Link
                            to={child.to}
                            onClick={onClose}
                            className="block py-2 pl-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
                          >
                            {child.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}

              <li>
                <Link
                  to={isAuthenticated ? "/account" : "/account/login"}
                  onClick={onClose}
                  className="flex items-center gap-2 py-4 border-b border-border/50 font-display text-xl transition-colors hover:text-primary"
                >
                  My Account
                </Link>
              </li>

              {isAdmin && (
                <li>
                  <Link
                    to="/admin"
                    onClick={onClose}
                    className="flex items-center gap-2 py-4 font-display text-xl text-primary"
                  >
                    <Shield className="h-5 w-5" strokeWidth={1.25} />
                    Admin Console
                  </Link>
                </li>
              )}
            </ul>
          </nav>

          {/* Footer */}
          <div className="border-t px-5 py-6 text-xs text-muted-foreground">
            <p>© {new Date().getFullYear()} Aster Decorz</p>
          </div>
        </div>
      </div>
    </>
  );
}
